import { defineEntityContract, type EntityByIdRequests, type EntityStateEvents, type QueuedChangeFailureEvents } from 'shared/channels/entity';
import type {
  ProjectCreateRequest,
  ProjectEditRequest,
} from 'shared/domain/messages/project/eventMessages';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { ProjectModel } from './types/model';

/**
 * Project events. A created or edited project comes as the model the worker
 * stored. Archive and restore outcomes (queued by older pages) stay `unknown`:
 * the HTTP queue answers them with `true`.
 */
export type ProjectEvents = EntityStateEvents & QueuedChangeFailureEvents & {
  [DomainMessagesTypes.allProjects]: { hasAll?: boolean; items: ProjectModel[] };
  [DomainMessagesTypes.createdEntity]: ProjectModel;
  [DomainMessagesTypes.updatedEntity]: ProjectModel;
  [DomainMessagesTypes.uploadedEntity]: unknown;
  [DomainMessagesTypes.deletedEntity]: unknown;
};

/**
 * One project for its details view, a download of a missing list, and the
 * project changes: each change resolves once queued, its outcome follows as
 * an event with its `uniqueId`.
 */
export type ProjectRequests = EntityByIdRequests<ProjectModel> & {
  /** Legacy PULL: downloads the list only when it is missing, and the permissions. */
  downloadWhenMissing: { request: undefined; response: void };
  create: { request: ProjectCreateRequest; response: void };
  edit: { request: ProjectEditRequest; response: void };
  /** The selected project's GDPR clause (HTML), read from the server. */
  getGdprClause: { request: undefined; response: string };
};

export const projectContract = defineEntityContract<ProjectModel, ProjectEvents, ProjectRequests>(
  'projectChannel',
  { collectionEvent: DomainMessagesTypes.allProjects }
);
